import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  MatLegacyDialogRef as MatDialogRef,
  MatLegacyDialog as MatDialog,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { of, Subject } from 'rxjs';
import { delay, takeUntil, debounceTime } from 'rxjs/operators';

import {
  EScannerType,
  IScannerResponse,
} from '../../../shared/interfaces/common.interface';

import { DialogConfig } from './../../../shared/configs/dialog.config';
import { ScannerServiceApiService } from './../../../shared/services/api/scanner-service-api.service';
import { UiWarningDialogComponent } from './../../../ui/components/ui-warning-dialog/ui-warning-dialog.component';

@Component({
  selector: 'lum-location-warehouse-app-scanner-popup-barcode',
  templateUrl: './location-warehouse-app-scanner-popup-barcode.component.html',
  styleUrls: ['./location-warehouse-app-scanner-popup-barcode.component.scss'],
})
export class LocationWarehouseAppScannerPopupBarcodeComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('searchInputRef')
  searchInputRef :ElementRef;

  loadingScan :boolean = false;

  isCallbackTriggered :boolean = false;

  manualSearchSubject = new Subject<string>();
  barcodeScanSubject = new Subject<string>();

  scan :any = {
    value: null,
  };

  private onDestroy$ = new Subject();

  constructor(
    public dialogRef :MatDialogRef<LocationWarehouseAppScannerPopupBarcodeComponent>,
    private dialog :MatDialog,
    private scannerServiceApiService :ScannerServiceApiService,
    private router :Router,
  ) {}

  ngOnInit() :void {
    this.onAutoBarcodeScan();
  }

  ngAfterViewInit() {
    this.searchInputRef?.nativeElement?.focus();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onBarcodeScanChange(event :Event) :void {
    if (this.isCallbackTriggered) {
      return;
    }

    this.isCallbackTriggered = true;
    this.loadingScan = true;

    const inputElement = event.target as HTMLInputElement;

    of(null)
      .pipe(delay(50)) // adjust delay as needed
      .subscribe(() => {
        const inputValue = inputElement.value;
        this.barcodeScanSubject.next(inputValue);
      });
  }

  onAutoBarcodeScan() :void {
    this.barcodeScanSubject
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((searchTerm :string) => {
        this.onScan();
      });
  }

  onScan() :void {
    if (this.scan.value) {
      this.scannerServiceApiService
        .getScannerData(this.scan.value)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response :IScannerResponse) => {
            if (response.type === EScannerType.UNKNOWN) {
              this.promptErrorNotFound();
            }

            if (response.id || response.orderNumber) {
              this.onScanned(response);
              this.dialogRef.close(response);
            }
            this.resetScanForm();
          },
          error: (response :any) => {
            this.promptErrorNotFound();
            this.resetScanForm();
          },
        });
    }
  }

  promptErrorNotFound() :void {
    const dialogExist = this.dialog.getDialogById('scanned-value-not-found');

    if (!dialogExist) {
      this.dialog
        .open(UiWarningDialogComponent, {
          id: 'scanned-value-not-found',
          data: {
            title: 'Not Found',
            message: 'The scanned value can not be found',
          },
          width: DialogConfig.sizes.small,
        })
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$));
    }
  }

  resetScanForm() :void {
    this.scan.value = null;
    this.loadingScan = false;
    this.isCallbackTriggered = false;
  }

  private onScanned(data :IScannerResponse) :void {
    switch (data.type) {
      case EScannerType.RFQ:
        this.router.navigate(['/warehouse-mobile-app/location-selection', data.id]);
        break;
      case EScannerType.LOCATION:
        this.router.navigate(['/warehouse-mobile-app/sku-selection', data.id]);
        break;
      case EScannerType.ORDER_NOT_IN_PICKING:
        this.router.navigateByUrl(
          `/shipping/posted-orders?warehouseGroup=${data.warehouseGroupId}&orderNumber=${data.orderNumber}`,
        );
        break;
      case EScannerType.ORDER_SHIPPED:
        this.router.navigateByUrl(
          `/shipping/posted-orders?warehouseGroup=${data.warehouseGroupId}&orderNumber=${data.orderNumber}`,
        );
        break;
      case EScannerType.ORDER_PICKLIST_CREATED:
        this.router.navigate(['/shipping/visual-picking', data.id]);
        break;
      case EScannerType.ORDER_PICKLIST_PICKED:
        this.router.navigate(['/shipping/picklists', data.id]);
        break;
      default:
        this.dialog.open(UiWarningDialogComponent, {
          data: {
            title: 'Warning',
            message: `Type: ${data.type} is not a valid type. Please scan a valid type to continue.`,
          },
          width: DialogConfig.sizes.small,
        });
        break;
    }
  }
}
